import React, { useRef, useCallback, useState, useEffect } from 'react'
import { IonText, IonImg, IonIcon, IonButton, IonSpinner } from '@ionic/react'
import { cameraOutline, imagesOutline } from 'ionicons/icons'
import FormField from '../forms/FormField'
import './ImageUploader.css'
import { FieldProps } from './common'
import useTranslation from 'hooks/useTranslation'
import { fetchRemoteFile } from 'services/network/api'
import useToast from 'hooks/useToast'
import offlinePlaceholder from 'logos/offline.png'
import useLocalConfiguration from 'hooks/useLocalConfiguration'
import FullImagePreview from './FullImagePreview'
import FileActionButtons from './FileActions'
import compressImage from 'common/compressImage'
import { useNetwork } from 'context/NetworkContext'
import OfflineState from 'ui/layouts/headers/OfflineState'
import useUser from 'hooks/useUser'
import { Camera, CameraSource, CameraResultType } from '@capacitor/camera'
import { Capacitor } from '@capacitor/core'

//const ALLOWED_IMAGE_EXTENSIONS = 'image/*'
const ALLOWED_IMAGE_EXTENSIONS = 'image/*;capture=camera'

export interface ImageUploaderProps extends FieldProps {
  value?:
    | File
    | {
        fieldId: string
        fieldType: string
        name: string
        size: string
        uri: string
      }
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const readFileAsBase64 = async (uploadedFile: File): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => {
      resolve(reader.result as string)
    }
    reader.onerror = reject
    reader.readAsDataURL(uploadedFile)
  })
}

const isRemoteFile = (value: any): boolean => {
  return Boolean(value && value.uri)
}

const ImageUploader: React.FC<ImageUploaderProps> = ({
  readOnly,
  name,
  label,
  hint,
  isRequired,
  hasError,
  errorText,
  onChange,
  value,
  tabIndex,
  fileId,
}) => {
  const t = useTranslation()
  const Toast = useToast()
  const { isOffline } = useNetwork()
  const { tryRefreshToken, needReLogin } = useUser()
  const inputFileRef = useRef<HTMLInputElement>(null)
  const { config } = useLocalConfiguration()
  const [imageDataURL, saveImageDataURL] = useState<string>()
  const [actionsVisible, setActionsVisible] = useState(false)
  const [previewVisible, setPreviewVisible] = useState(false)

  const [remoteImageState, setRemoteImageState] = useState({
    isFetching: false,
    error: false,
  })

  const cancelController = useRef(new AbortController())

  const handleFileInputClick = useCallback(async () => {
    // If it is a native platform, use the Camera plugin from Capacitor
    if (Capacitor.isNativePlatform()) {
      try {
        const image = await Camera.getPhoto({
          quality: 90,
          allowEditing: false,
          resultType: CameraResultType.Base64,
          source: CameraSource.Prompt, // Allows to choose between camera or gallery
        })

        if (image.base64String) {
          // Convert base64 to File
          const byteCharacters = atob(image.base64String)
          const byteNumbers = new Array(byteCharacters.length)
          for (let i = 0; i < byteCharacters.length; i++) {
            byteNumbers[i] = byteCharacters.charCodeAt(i)
          }
          const byteArray = new Uint8Array(byteNumbers)
          const blob = new Blob([byteArray], { type: `image/${image.format}` })

          // Generate unique filename with timestamp to avoid conflicts
          const timestamp = Date.now()
          const fileName = `photo_${timestamp}.${image.format}`

          const file = new File([blob], fileName, {
            type: `image/${image.format}`,
            lastModified: timestamp,
          })

          const reducedFile = await compressImage(file)
          onChange(name, reducedFile)
          const imageDataURL = URL.createObjectURL(reducedFile)
          saveImageDataURL(imageDataURL)
          Boolean(config.autoDownloadImages) &&
            downloadImage(imageDataURL, reducedFile)
        }
      } catch (error: any) {
        if (error.message !== 'User cancelled photos app') {
          console.error('Error accessing camera/gallery:', error)
          Toast.warning(
            t('notifications.permisisonDenied') ||
              'Error al acceder a la cámara o galería',
            3000,
          )
        }
      }
    } else {
      // In web/PWA, use the traditional input file
      inputFileRef.current?.click()
    }
  }, [onChange, name, config.autoDownloadImages, Toast, t])

  const handleUpload = useCallback(
    async (e: any) => {
      const file = e.target.files[0]
      if (file) {
        const reducedFile = await compressImage(file)

        onChange(name, reducedFile)
        const imageDataURL = URL.createObjectURL(reducedFile)
        saveImageDataURL(imageDataURL)
        Boolean(config.autoDownloadImages) &&
          downloadImage(imageDataURL, reducedFile)
      }
    },
    [onChange, name, config.autoDownloadImages],
  )

  const handleDeleteFile = useCallback(() => {
    onChange(name, undefined)
    saveImageDataURL(undefined)
  }, [name, onChange])

  const handleDownload = useCallback(() => {
    if (imageDataURL != null && value != null) {
      downloadImage(imageDataURL, value)
    }
  }, [imageDataURL, value])

  useEffect(() => {
    if (!value) return

    const loadImage = async () => {
      try {
        if (value instanceof File) {
          const imageDataURL = URL.createObjectURL(value)
          saveImageDataURL(imageDataURL)
        } else if (value.uri) {
          if (isOffline) {
            Toast.warning(t('notifications.offlineLoadImage'), 3000)
            setRemoteImageState({
              isFetching: false,
              error: true,
            })
            return
          }
          if (needReLogin()) {
            await tryRefreshToken().catch((e: Error) => {
              console.error('Error refreshing token', e)
            })
          }
          setRemoteImageState({
            isFetching: true,
            error: false,
          })
          fetchRemoteFile(value.uri, cancelController.current.signal)
            .then((x: any) => URL.createObjectURL(x))
            .then((url) => {
              saveImageDataURL(url)
              setRemoteImageState({
                isFetching: false,
                error: false,
              })
            })
            .catch((err: Error) => {
              if (err.name !== 'AbortError') {
                setRemoteImageState({
                  isFetching: false,
                  error: true,
                })
                Toast.warning(t('notifications.failedLoadImage'), 3000)
              }
            })
        }
      } catch (err) {
        console.error('Error loading image', err)
      }
    }

    loadImage()
  }, [value, Toast, t, isOffline, tryRefreshToken, needReLogin])

  // revoke (clean memory)
  useEffect(() => {
    let controller = cancelController.current
    return () => {
      if (imageDataURL) {
        URL.revokeObjectURL(imageDataURL)
      }
      if (controller) {
        controller.abort()
      }
    }
  }, [imageDataURL])

  return (
    <FormField
      label={label}
      hint={hint}
      hasError={hasError}
      errorText={errorText}
      isRequired={isRequired}
      tabIndex={tabIndex}
      fileId={fileId}
    >
      <OfflineState
        visible={isRemoteFile(value)}
        message={t('notifications.offlineLoadImage')}
      />

      <div className="image-uploader-trigger">
        {imageDataURL ? (
          <p
            className="image-uploader-picture"
            onClick={() => setActionsVisible(true)}
          >
            <IonImg
              src={imageDataURL}
              style={{ maxHeight: '300px' }}
              onScroll={console.log}
            />
          </p>
        ) : (
          <IonButton
            className="image-uploader-button"
            fill="clear"
            onClick={handleFileInputClick}
            disabled={readOnly}
          >
            <IonText className="image-uploader-placeholder">
              {!remoteImageState.error && (
                <IonIcon
                  icon={readOnly ? imagesOutline : cameraOutline}
                  size="large"
                  className="ion-margin-vertical"
                />
              )}
              {remoteImageState.error ? (
                <IonImg src={offlinePlaceholder} />
              ) : (
                <IonText className="image-uploader-placeholder-text">
                  {remoteImageState.isFetching ? (
                    <IonSpinner />
                  ) : readOnly ? (
                    t('withoutValue')
                  ) : (
                    t('buttons.takePicture')
                  )}
                </IonText>
              )}
            </IonText>
          </IonButton>
        )}
      </div>
      <input
        type="file"
        className="hidden-input-file"
        ref={inputFileRef}
        onChange={handleUpload}
        accept={ALLOWED_IMAGE_EXTENSIONS}
        //capture="environment"
        hidden
      />
      <FileActionButtons
        open={actionsVisible}
        value={value}
        readOnly={readOnly}
        onDownload={handleDownload}
        onDelete={handleDeleteFile}
        onModifyClick={handleFileInputClick}
        onPreviewClick={() => setPreviewVisible(true)}
        onDismiss={() => setActionsVisible(false)}
      />
      {value && (
        <FullImagePreview
          isOpen={previewVisible}
          title={value.name}
          onClose={() => setPreviewVisible(false)}
          src={imageDataURL}
        />
      )}
    </FormField>
  )
}

export default ImageUploader
function downloadImage(
  imageDataURL: string,
  value:
    | File
    | {
        fieldId: string
        fieldType: string
        name: string
        size: string
        uri: string
      },
) {
  const downloadLink = document.createElement('a')
  downloadLink.href = imageDataURL
  downloadLink.download = value.name
  downloadLink.click()
}
